import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LayoutGrid, Inbox, Image as ImageIcon, Calendar, Star, GraduationCap,
  Sparkles, LogOut, Loader2, Trash2,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import EntityManager from "../../components/admin/EntityManager";
import ImageUpload from "../../components/admin/ImageUpload";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

const NAV = [
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "courses", label: "Courses", icon: GraduationCap },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
  { key: "events", label: "Events", icon: Calendar },
  { key: "testimonials", label: "Testimonials", icon: Star },
  { key: "hero", label: "Hero & Stats", icon: Sparkles },
];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState("enquiries");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ enquiries: [], courses: [], gallery: [], events: [], testimonials: [], settings: {} });

  const load = async () => {
    try {
      const [enquiries, courses, gallery, events, testimonials, settings] = await Promise.all([
        api.listEnquiries(), api.getCourses(), api.getGallery(), api.getEvents(), api.getTestimonials(), api.getSettings(),
      ]);
      setData({ enquiries, courses, gallery, events, testimonials, settings });
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-primary text-primary-foreground flex flex-col fixed inset-y-0 left-0 z-20">
        <div className="p-6 border-b border-white/10 flex items-center gap-3">
          <img src="/rythmbara-logo.png" alt="Rythmbara" className="h-11 w-11 rounded-full shrink-0" />
          <div className="min-w-0">
            <p className="font-serif text-2xl leading-none">Rythm<span className="text-gold">bara</span></p>
            <p className="text-xs text-white/50 mt-1 truncate">{admin?.email}</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.key} onClick={() => setTab(n.key)} data-testid={`admin-nav-${n.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-colors ${tab === n.key ? "bg-white/15 text-gold" : "text-white/70 hover:bg-white/10"}`}>
                <Icon className="h-4 w-4" /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm text-white/70 hover:bg-white/10">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 ml-64 p-8 md:p-12 overflow-y-auto">
        {loading ? (
          <div className="grid place-items-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <>
            {tab === "enquiries" && <Enquiries items={data.enquiries} reload={load} />}
            {tab === "courses" && (
              <EntityManager type="courses" title="Courses" items={data.courses} onChanged={load}
                fields={[
                  { key: "name", label: "Name" }, { key: "category", label: "Category" },
                  { key: "description", label: "Description", type: "textarea" },
                  { key: "image", label: "Image", type: "image" }, { key: "order", label: "Order", type: "number" },
                ]} />
            )}
            {tab === "gallery" && (
              <EntityManager type="gallery" title="Gallery" items={data.gallery} onChanged={load}
                fields={[
                  { key: "title", label: "Title" }, { key: "category", label: "Category" },
                  { key: "image", label: "Image", type: "image" }, { key: "order", label: "Order", type: "number" },
                ]} />
            )}
            {tab === "events" && (
              <EntityManager type="events" title="Events" items={data.events} onChanged={load}
                fields={[
                  { key: "title", label: "Title" }, { key: "date", label: "Date (YYYY-MM-DD)" },
                  { key: "location", label: "Location" }, { key: "description", label: "Description", type: "textarea" },
                  { key: "image", label: "Image", type: "image" }, { key: "order", label: "Order", type: "number" },
                ]} />
            )}
            {tab === "testimonials" && (
              <EntityManager type="testimonials" title="Testimonials" items={data.testimonials} onChanged={load}
                fields={[
                  { key: "name", label: "Name" }, { key: "role", label: "Role" },
                  { key: "quote", label: "Quote", type: "textarea" }, { key: "rating", label: "Rating (1-5)", type: "number" },
                  { key: "order", label: "Order", type: "number" },
                ]} />
            )}
            {tab === "hero" && <HeroSettings settings={data.settings} reload={load} />}
          </>
        )}
      </main>
    </div>
  );
}

function Enquiries({ items, reload }) {
  const del = async (id) => {
    try {
      await api.deleteEnquiry(id);
      toast.success("Enquiry deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };
  return (
    <div data-testid="manager-enquiries">
      <h2 className="font-serif text-3xl text-foreground mb-1">Enquiries</h2>
      <p className="text-sm text-muted-foreground mb-6">{items.length} total submissions</p>
      <div className="overflow-x-auto border border-border rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              {["Name", "Phone", "Email", "Age", "Style", "Message", "Date", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-medium text-muted-foreground uppercase text-xs tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-border hover:bg-secondary/50" data-testid={`enquiry-row-${e.id}`}>
                <td className="px-4 py-3 font-medium text-foreground">{e.name}</td>
                <td className="px-4 py-3">{e.phone}</td>
                <td className="px-4 py-3">{e.email}</td>
                <td className="px-4 py-3">{e.age || "-"}</td>
                <td className="px-4 py-3">{e.dance_style || "-"}</td>
                <td className="px-4 py-3 max-w-[220px] truncate">{e.message || "-"}</td>
                <td className="px-4 py-3 whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleDateString("en-IN") : "-"}</td>
                <td className="px-4 py-3">
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => del(e.id)} data-testid={`delete-enquiry-${e.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No enquiries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HeroSettings({ settings, reload }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings(form);
      toast.success("Settings saved");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const textFields = [
    { key: "hero_tagline", label: "Hero Tagline" },
    { key: "hero_title", label: "Hero Title" },
    { key: "hero_subtitle", label: "Hero Subtitle", type: "textarea" },
    { key: "hero_image", label: "Hero Image URL" },
  ];
  const statFields = [
    { key: "stat_years", label: "Years" }, { key: "stat_students", label: "Students" },
    { key: "stat_styles", label: "Dance Forms" }, { key: "stat_awards", label: "Awards" },
  ];

  return (
    <div data-testid="manager-hero" className="max-w-2xl">
      <h2 className="font-serif text-3xl text-foreground mb-1">Hero &amp; Statistics</h2>
      <p className="text-sm text-muted-foreground mb-6">Update the homepage banner and stat counters</p>
      <div className="space-y-4 bg-card border border-border p-6 rounded-sm">
        {textFields.map((f) => (
          <div key={f.key}>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</Label>
            {f.key === "hero_image" ? (
              <div className="mt-1.5"><ImageUpload value={form.hero_image} onChange={(v) => setForm({ ...form, hero_image: v })} testid="hero" /></div>
            ) : f.type === "textarea" ? (
              <Textarea className="mt-1.5" value={form[f.key] ?? ""} onChange={set(f.key)} data-testid={`hero-${f.key}`} />
            ) : (
              <Input className="mt-1.5" value={form[f.key] ?? ""} onChange={set(f.key)} data-testid={`hero-${f.key}`} />
            )}
          </div>
        ))}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
          {statFields.map((f) => (
            <div key={f.key}>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</Label>
              <Input className="mt-1.5" value={form[f.key] ?? ""} onChange={set(f.key)} data-testid={`hero-${f.key}`} />
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-hero-btn" className="bg-primary text-primary-foreground mt-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
